import i18next from 'i18next';
import { authAPI } from './auth';
import type { DeepAnalysisFullSession } from './api';

const API_BASE = '/api/v1/admin';

// Types
export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  tier: 'free' | 'pro' | 'max';
  isAdmin: boolean;
  isBanned: boolean;
  emailVerified: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface UserDetails extends AdminUser {
  subscription: {
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    subscriptionStatus: string | null;
    currentPeriodEnd: string | null;
  };
  usage: {
    totalSearches: number;
    totalAnalyses: number;
    bookmarksCount: number;
    collectionsCount: number;
  };
}

export interface PaginatedUsers {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface OverviewStats {
  users: {
    total: number;
    free: number;
    pro: number;
    newThisMonth: number;
  };
  revenue: {
    mrr: number;
    totalRevenue: number;
  };
  usage: {
    searchesToday: number;
    analysesToday: number;
    totalSearches: number;
    totalAnalyses: number;
  };
}

export interface TimeSeriesData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
  }[];
}

export interface RevenueStats {
  mrr: number;
  totalRevenue: number;
  byMonth: TimeSeriesData;
}

export interface UsageByTier {
  free: { searches: number; analyses: number };
  pro: { searches: number; analyses: number };
  byDay: TimeSeriesData;
}

export interface AuditLog {
  id: string;
  adminUserId: string;
  adminEmail: string;
  action: string;
  targetType: string;
  targetId: string | null;
  details: Record<string, unknown>;
  ipAddress: string;
  createdAt: string;
}

export interface PaginatedAuditLogs {
  logs: AuditLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Deep Analysis Types
export interface AnalysisSession {
  id: string;
  userId: string;
  userEmail: string;
  documentPreview: string;
  status: string;
  searchRoundsCompleted: number;
  totalJudgmentsFound: number;
  createdAt: string;
  completedAt: string | null;
  evaluation: {
    score: number;
    grade: string;
    legalBasesCoverage: number;
    authorityScore: number;
    balanceScore: number;
    evaluatedAt: string;
  } | null;
  feedback: {
    rating: number;
    comment: string | null;
    submittedAt: string;
  } | null;
}

export interface PaginatedAnalysisSessions {
  sessions: AnalysisSession[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AnalysisStats {
  sessions: {
    total: number;
    completed: number;
    failed: number;
    today: number;
    thisWeek: number;
    avgDurationSeconds: number;
    avgSearchRounds: number;
    avgJudgmentsFound: number;
  };
  evaluations: {
    total: number;
    avgScore: number;
    avgLegalCoverage: number;
    avgAuthority: number;
    avgBalance: number;
    gradeDistribution: Record<string, number>;
  };
  feedback: {
    total: number;
    avgRating: number;
    ratingDistribution: Record<number, number>;
  };
  correlation: {
    scoreRatingCorrelation: number | null;
  };
}

export interface AnalysisSessionDetails {
  session: {
    id: string;
    userId: string;
    userEmail: string;
    document: string;
    documentPreview: string;
    userContext: string | null;
    status: string;
    searchRoundsCompleted: number;
    totalJudgmentsFound: number;
    report: string | null;
    createdAt: string;
    completedAt: string | null;
  };
  evaluation: {
    score: number;
    grade: string;
    legalBasesCoverage: number;
    authorityScore: number;
    balanceScore: number;
    recencyScore: number;
    confidenceScore: number;
    searchEfficiency: number;
    issues: unknown[];
    suggestions: string[];
  } | null;
  feedback: {
    rating: number;
    categoryRatings: Record<string, number> | null;
    comment: string | null;
    helpful: string[] | null;
    improvements: string[] | null;
  } | null;
}

// Analysis Review Types
export interface ToolCallSummary {
  name: string;
  arguments: Record<string, unknown>;
  durationMs: number;
  hasError: boolean;
  error?: string;
  resultPreview: string;
}

export interface TurnSummary {
  turnNumber: number;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  tokensInput: number;
  tokensOutput: number;
  messagesCount: number;
  responseTextPreview: string;
  responseTextLength: number;
  finishReason: string;
  toolCallsCount: number;
  toolCalls: ToolCallSummary[];
  understandingStateAfter: Record<string, unknown>;
}

export interface LoggedMetadata {
  sessionId: string;
  userId?: string;
  documentHash: string;
  documentSize: number;
  startedAt: string;
  completedAt?: string;
  totalDurationMs?: number;
  totalTurns: number;
  totalTokensInput: number;
  totalTokensOutput: number;
  status: string;
  modelId: string;
  promptVersion: string;
  totalToolCalls: number;
  uniqueToolsUsed: string[];
  finalJudgmentsCount: number;
}

export interface SessionTurnsResponse {
  sessionId: string;
  metadata: LoggedMetadata | null;
  turns: TurnSummary[];
  message?: string;
}

export interface TurnDetails {
  turnNumber: number;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  tokensInput: number;
  tokensOutput: number;
  messagesCount: number;
  systemPrompt: string | null;
  responseText: string;
  finishReason: string;
  toolCalls: {
    name: string;
    arguments: Record<string, unknown>;
    result: unknown;
    error?: string;
    durationMs: number;
    executedAt: string;
  }[];
  understandingStateAfter: Record<string, unknown>;
}

export interface RefinementNote {
  id: string;
  sessionId: string;
  turnIndex: number | null;
  issueType: 'prompt' | 'search' | 'reasoning' | 'output' | 'tool' | 'other';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  suggestedFix: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  fixedInVersion: string | null;
  fixedAt: string | null;
  verifiedBy: string | null;
  verifiedAt: string | null;
}

export interface UnreviewedSession {
  id: string;
  userId: string;
  documentPreview: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
  hasLogs: boolean;
  evaluationScore: number | null;
  tokensTotal: number;
  costPln: number;
}

export interface HiddenSession extends UnreviewedSession {
  hiddenAt: string;
}

// Token usage and cost response
export interface TokenUsageResponse {
  sessionId: string;
  modelId: string | null;
  tokensInput: number;
  tokensOutput: number;
  tokensTotal: number;
  totalTurns: number;
  costUsd: {
    input: number;
    output: number;
    total: number;
  };
  costPln: {
    input: number;
    output: number;
    total: number;
  };
  exchangeRate: number;
  message?: string;
}

// Conversation timeline item types for preview
export interface ConversationTimelineItem {
  id: string;
  type: 'user_input' | 'progress_block' | 'clarification_request' | 'report' | 'followup_question' | 'followup_response';
  createdAt: string;
  roundNumber: number;
  content?: string;
  markdown?: string;
  steps?: unknown[];
  questions?: unknown[];
  isComplete?: boolean;
  isStreaming?: boolean;
}

// Session preview response
export interface SessionPreviewResponse {
  sessionId: string;
  status: string;
  timeline: ConversationTimelineItem[];
  tokensUsed: number;
  createdAt: string;
  completedAt: string | null;
  hasReport: boolean;
}

export interface SystemHealth {
  status: string;
  database: {
    pool: {
      totalCount: number;
      idleCount: number;
      waitingCount: number;
    };
    size: string;
    tables: { table_name: string; total_size: string }[];
    indexes: { index_name: string; size: string }[];
  };
  process: {
    uptime: number;
    memoryUsage: NodeJS.MemoryUsage;
    nodeVersion: string;
  };
  timestamp: string;
}

export interface DatabaseStats {
  judgments: {
    total: number;
    byCourtType: Record<string, number>;
  };
  chunks: {
    total: number;
    withEmbeddings: number;
    byType: Record<string, number>;
  };
  timestamp: string;
}

// Process Monitoring Types
export interface ProcessProgress {
  current: number;
  total: number;
  percent: number;
  message?: string;
}

export interface ProcessRun {
  id: string;
  processType: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'stopped';
  triggeredBy: string | null;
  triggerType: 'manual' | 'scheduled' | 'system';
  startedAt: string | null;
  completedAt: string | null;
  progress: ProcessProgress;
  result: Record<string, unknown>;
  errorMessage: string | null;
  containerId: string | null;
  pid: number | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface ProcessStatus {
  processType: string;
  displayName: string;
  description: string;
  status: 'running' | 'idle' | 'pending' | 'completed' | 'failed' | 'stopped';
  currentRun: ProcessRun | null;
  lastCompletedRun: ProcessRun | null;
  nextScheduledRun: string | null;
  requiresDocker: boolean;
}

export interface PendingWork {
  judgmentsWithoutEmbeddings: number;
  chunksWithoutEmbeddings: number;
  judgmentsNeedingReprocessing: number;
  filesAwaitingIngestion: number;
  cbosaFilesAwaitingIngestion: number;
}

export interface DataFreshness {
  saos: {
    lastSync: string | null;
    daysSinceSync: number | null;
    totalJudgments: number;
  };
  cbosa: {
    lastSync: string | null;
    daysSinceSync: number | null;
    totalJudgments: number;
    isRunning: boolean;
    progress?: ProcessProgress;
  };
}

export interface DiskUsage {
  rawData: {
    path: string;
    sizeBytes: number;
    sizeFormatted: string;
    fileCount: number;
  };
  cbosaData: {
    path: string;
    sizeBytes: number;
    sizeFormatted: string;
    fileCount: number;
  };
  database: {
    sizeFormatted: string;
  };
  logs: {
    path: string;
    sizeBytes: number;
    sizeFormatted: string;
  };
}

export interface ErrorLogEntry {
  timestamp: string;
  processType: string;
  level: 'error' | 'warn';
  message: string;
  details?: Record<string, unknown>;
}

export interface SaosSyncStatus {
  saos: {
    total: number;
    lastJudgmentDate: string | null;
  };
  local: {
    total: number;
    lastJudgmentDate: string | null;
    lastSyncDate: string | null;
  };
  comparison: {
    difference: number;
    percentSynced: number;
    needsSync: boolean;
    recommendation: 'none' | 'incremental' | 'full';
  };
  checkedAt: string;
}

export interface SaosSyncResult {
  message: string;
  fetched: number;
  newJudgments: number;
  updatedJudgments: number;
  startDate: string;
  endDate: string;
}

// LLM Model Settings Types
export interface ModelSetting {
  id: string;
  settingKey: string;
  settingValue: {
    modelId?: string;
    enabled?: boolean;
  };
  description: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface AvailableModel {
  id: string;
  displayName: string;
  provider: 'openai' | 'anthropic';
  description: string;
}

class AdminAPI {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = authAPI.getAccessToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || i18next.t('errors.defaultError'));
    }

    return data;
  }

  // User Management
  async getUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
    tier?: 'free' | 'pro' | 'max';
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  } = {}): Promise<PaginatedUsers> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.search) searchParams.set('search', params.search);
    if (params.tier) searchParams.set('tier', params.tier);
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    const query = searchParams.toString();
    return this.request<PaginatedUsers>(`/users${query ? `?${query}` : ''}`);
  }

  async getUser(userId: string): Promise<UserDetails> {
    return this.request<UserDetails>(`/users/${userId}`);
  }

  async updateUser(
    userId: string,
    updates: {
      tier?: 'free' | 'pro' | 'max';
      isBanned?: boolean;
      isAdmin?: boolean;
    }
  ): Promise<AdminUser> {
    return this.request<AdminUser>(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // Analytics
  async getOverview(): Promise<OverviewStats> {
    return this.request<OverviewStats>('/analytics/overview');
  }

  async getUserGrowth(period: string = '30d'): Promise<TimeSeriesData> {
    return this.request<TimeSeriesData>(`/analytics/users?period=${period}`);
  }

  async getRevenue(period: string = '30d'): Promise<RevenueStats> {
    return this.request<RevenueStats>(`/analytics/revenue?period=${period}`);
  }

  async getUsageByTier(period: string = '30d'): Promise<UsageByTier> {
    return this.request<UsageByTier>(`/analytics/usage?period=${period}`);
  }

  // Audit Logs
  async getAuditLogs(params: {
    page?: number;
    limit?: number;
    adminUserId?: string;
    action?: string;
    targetType?: string;
  } = {}): Promise<PaginatedAuditLogs> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.adminUserId) searchParams.set('adminUserId', params.adminUserId);
    if (params.action) searchParams.set('action', params.action);
    if (params.targetType) searchParams.set('targetType', params.targetType);

    const query = searchParams.toString();
    return this.request<PaginatedAuditLogs>(`/audit-logs${query ? `?${query}` : ''}`);
  }

  // System
  async getSystemHealth(): Promise<SystemHealth> {
    return this.request<SystemHealth>('/system/health');
  }

  async getDatabaseStats(): Promise<DatabaseStats> {
    return this.request<DatabaseStats>('/stats');
  }

  // Process Monitoring
  async getProcessStatuses(): Promise<{ processes: ProcessStatus[] }> {
    return this.request<{ processes: ProcessStatus[] }>('/processes');
  }

  async getProcessStatus(processType: string): Promise<ProcessStatus> {
    return this.request<ProcessStatus>(`/processes/${processType}`);
  }

  async getProcessHistory(processType: string, limit: number = 20): Promise<{ runs: ProcessRun[] }> {
    return this.request<{ runs: ProcessRun[] }>(`/processes/${processType}/history?limit=${limit}`);
  }

  async getProcessLogs(processType: string): Promise<{ logs: string[] }> {
    return this.request<{ logs: string[] }>(`/processes/${processType}/logs`);
  }

  async triggerProcess(processType: string, config?: Record<string, unknown>): Promise<{ message: string; run: ProcessRun }> {
    return this.request<{ message: string; run: ProcessRun }>(`/processes/${processType}/trigger`, {
      method: 'POST',
      body: JSON.stringify({ config }),
    });
  }

  async stopProcess(processType: string): Promise<{ message: string; success: boolean }> {
    return this.request<{ message: string; success: boolean }>(`/processes/${processType}/stop`, {
      method: 'POST',
    });
  }

  async getPendingWork(): Promise<PendingWork> {
    return this.request<PendingWork>('/system/pending-work');
  }

  async getDataFreshness(): Promise<DataFreshness> {
    return this.request<DataFreshness>('/system/data-freshness');
  }

  async getDiskUsage(): Promise<DiskUsage> {
    return this.request<DiskUsage>('/system/disk');
  }

  async getRecentErrors(limit: number = 20): Promise<{ errors: ErrorLogEntry[] }> {
    return this.request<{ errors: ErrorLogEntry[] }>(`/system/errors?limit=${limit}`);
  }

  async getSaosSyncStatus(): Promise<SaosSyncStatus> {
    return this.request<SaosSyncStatus>('/system/saos-sync-status');
  }

  async triggerSaosSync(): Promise<SaosSyncResult> {
    return this.request<SaosSyncResult>('/system/saos-sync', {
      method: 'POST',
    });
  }

  // Deep Analysis Monitoring
  async getAnalysisStats(): Promise<AnalysisStats> {
    return this.request<AnalysisStats>('/analysis/stats');
  }

  async getAnalysisSessions(params: {
    page?: number;
    limit?: number;
    minScore?: number;
    maxScore?: number;
    hasEvaluation?: boolean;
    hasFeedback?: boolean;
  } = {}): Promise<PaginatedAnalysisSessions> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.minScore !== undefined) searchParams.set('minScore', String(params.minScore));
    if (params.maxScore !== undefined) searchParams.set('maxScore', String(params.maxScore));
    if (params.hasEvaluation !== undefined) searchParams.set('hasEvaluation', String(params.hasEvaluation));
    if (params.hasFeedback !== undefined) searchParams.set('hasFeedback', String(params.hasFeedback));

    const query = searchParams.toString();
    return this.request<PaginatedAnalysisSessions>(`/analysis/sessions${query ? `?${query}` : ''}`);
  }

  async getAnalysisSession(sessionId: string): Promise<AnalysisSessionDetails> {
    return this.request<AnalysisSessionDetails>(`/analysis/sessions/${sessionId}`);
  }

  // Analysis Review & Refinement
  async getSessionTurns(sessionId: string): Promise<SessionTurnsResponse> {
    return this.request<SessionTurnsResponse>(`/analysis/sessions/${sessionId}/turns`);
  }

  async getSessionTurn(sessionId: string, turnIndex: number): Promise<TurnDetails> {
    return this.request<TurnDetails>(`/analysis/sessions/${sessionId}/turns/${turnIndex}`);
  }

  async getSessionPrompt(sessionId: string, turnIndex: number): Promise<{ sessionId: string; turnIndex: number; systemPrompt: string }> {
    return this.request<{ sessionId: string; turnIndex: number; systemPrompt: string }>(`/analysis/sessions/${sessionId}/turns/${turnIndex}/prompt`);
  }

  async submitReview(sessionId: string, data: { qualityRating?: number; reviewNotes?: string }): Promise<{ success: boolean; session: unknown }> {
    return this.request<{ success: boolean; session: unknown }>(`/analysis/sessions/${sessionId}/review`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSessionNotes(sessionId: string): Promise<{ sessionId: string; notes: RefinementNote[] }> {
    return this.request<{ sessionId: string; notes: RefinementNote[] }>(`/analysis/sessions/${sessionId}/notes`);
  }

  async createNote(sessionId: string, note: {
    turnIndex?: number;
    issueType: string;
    severity?: string;
    description: string;
    suggestedFix?: string;
  }): Promise<RefinementNote> {
    return this.request<RefinementNote>(`/analysis/sessions/${sessionId}/notes`, {
      method: 'POST',
      body: JSON.stringify(note),
    });
  }

  async updateNote(noteId: string, updates: Partial<{
    description: string;
    suggestedFix: string;
    severity: string;
    issueType: string;
    fixedInVersion: string;
  }>): Promise<RefinementNote> {
    return this.request<RefinementNote>(`/analysis/notes/${noteId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteNote(noteId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/analysis/notes/${noteId}`, {
      method: 'DELETE',
    });
  }

  async getUnfixedNotes(params?: { issueType?: string; severity?: string; limit?: number }): Promise<{ notes: RefinementNote[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.issueType) searchParams.set('issueType', params.issueType);
    if (params?.severity) searchParams.set('severity', params.severity);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    return this.request<{ notes: RefinementNote[]; total: number }>(`/analysis/notes/unfixed${query ? `?${query}` : ''}`);
  }

  async markNoteFixed(noteId: string, fixedInVersion: string): Promise<RefinementNote> {
    return this.request<RefinementNote>(`/analysis/notes/${noteId}/fix`, {
      method: 'POST',
      body: JSON.stringify({ fixedInVersion }),
    });
  }

  async markNoteVerified(noteId: string): Promise<RefinementNote> {
    return this.request<RefinementNote>(`/analysis/notes/${noteId}/verify`, {
      method: 'POST',
    });
  }

  async getUnreviewedSessions(limit: number = 20): Promise<{ sessions: UnreviewedSession[]; total: number }> {
    return this.request<{ sessions: UnreviewedSession[]; total: number }>(`/analysis/sessions/unreviewed?limit=${limit}`);
  }

  // Token Usage & Preview
  async getSessionTokenUsage(sessionId: string): Promise<TokenUsageResponse> {
    return this.request<TokenUsageResponse>(`/analysis/sessions/${sessionId}/token-usage`);
  }

  /**
   * Get full session data for preview (same format as user API)
   * Returns DeepAnalysisFullSession for use with mapFullSessionToConversationSession
   */
  async getSessionPreview(sessionId: string): Promise<DeepAnalysisFullSession> {
    return this.request<DeepAnalysisFullSession>(`/analysis/sessions/${sessionId}/preview`);
  }

  // Session visibility (hide/unhide)
  async hideSession(sessionId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/analysis/sessions/${sessionId}/hide`, {
      method: 'POST',
    });
  }

  async unhideSession(sessionId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/analysis/sessions/${sessionId}/unhide`, {
      method: 'POST',
    });
  }

  async getHiddenSessions(limit: number = 50): Promise<{ sessions: HiddenSession[]; total: number }> {
    return this.request<{ sessions: HiddenSession[]; total: number }>(`/analysis/sessions-hidden?limit=${limit}`);
  }

  // LLM Model Settings
  async getModelSettings(): Promise<{ settings: ModelSetting[] }> {
    return this.request<{ settings: ModelSetting[] }>('/models');
  }

  async getAvailableModels(): Promise<{ models: AvailableModel[] }> {
    return this.request<{ models: AvailableModel[] }>('/models/available');
  }

  async updateModelSetting(key: string, value: { modelId?: string; enabled?: boolean }): Promise<ModelSetting> {
    return this.request<ModelSetting>(`/models/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }
}

export const adminAPI = new AdminAPI();
